// ================================
// CUSTOMER TRACK PAGE - PHASE 2
// Shows wait time only, no queue info
// ================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';
import ChatThread from '../../components/shared/ChatThread';

const STATUSES = ['Pending', 'Assigned', 'Driver Arrived', 'Delivered'];

export default function CustomerTrack() {
  const navigate          = useNavigate();
  const socket            = useSocket();
  const [orders, setOrders]   = useState([]);
  const [etas, setEtas]       = useState({});
  const [loading, setLoading] = useState(true);
  const [chatOid, setChatOid] = useState(null);
  const [unread, setUnread] = useState({ directThread: null, orderThreads: {}, totalUnread: 0 });

  useEffect(() => {
    fetchOrders();
    fetchUnread();
    // Fallback polling — without this, a stale masked relay number (e.g.
    // after a server restart mid-delivery) could sit on screen until some
    // other status-change event happens to refresh it. The driver's queue
    // already re-checks every 15s for the same reason; this matches it.
    const poll = setInterval(() => { fetchOrders(); fetchUnread(); }, 15000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!socket) return;
    // Real-time order status updates
    socket.on('order:delivered',  () => fetchOrders());
    socket.on('order:updated',    () => fetchOrders());
    // Real-time ETA updates from driver location pings
    socket.on('eta:updated', ({ oid, minutes, showLive, message }) => {
      setEtas(prev => ({ ...prev, [oid]: { minutes, showLive, message } }));
    });
    socket.on('message:new', () => fetchUnread());
    return () => {
      socket.off('order:delivered');
      socket.off('order:updated');
      socket.off('eta:updated');
      socket.off('message:new');
    };
  }, [socket]);

  async function fetchUnread() {
    try {
      const res = await axios.get('/api/messages/my-unread');
      setUnread(res.data);
    } catch (e) {}
  }

  async function fetchOrders() {
    try {
      const res = await axios.get('/api/orders/my-orders');
      setOrders(res.data.orders || []);
      // Fetch ETA for active orders
      const active = (res.data.orders || []).filter(o => o.status !== 'Delivered' && o.status !== 'Cancelled' && o.assigned);
      for (const order of active) {
        fetchETA(order.oid);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function fetchETA(oid) {
    try {
      const res = await axios.get(`/api/orders/${oid}/eta`);
      setEtas(prev => ({ ...prev, [oid]: res.data }));
    } catch(e) { console.error(e); }
  }

  function getStepIndex(status) {
    const s = String(status).toLowerCase().trim();
    if (s === 'pending')            return 0;
    if (s === 'assigned')           return 1;
    if (s === 'driver arrived')     return 2;
    if (s === 'delivered')          return 3;
    if (s === 'attempted delivery') return 3;
    return 0;
  }

  const activeOrders    = orders.filter(o => o.status !== 'Delivered' && o.status !== 'Attempted Delivery' && o.status !== 'Cancelled');
  const deliveredOrders = orders.filter(o => o.status === 'Delivered' || o.status === 'Attempted Delivery' || o.status === 'Cancelled').slice(0, 5);

  return (
    <div className="app-shell">
      <div style={{ padding:'18px 22px 14px', borderBottom:'1px solid var(--border)' }}>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:22, letterSpacing:2 }}>Order Status</h2>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>
        {loading ? (
          <div className="loading">Loading orders...</div>
        ) : activeOrders.length === 0 && deliveredOrders.length === 0 ? (
          <div style={{ textAlign:'center', marginTop:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>📭</div>
            <p style={{ color:'var(--text-secondary)', fontSize:14 }}>No orders yet.</p>
            <button className="btn-primary" style={{ marginTop:24, maxWidth:200, margin:'24px auto 0', display:'block' }}
              onClick={() => navigate('/order')}>Place an Order</button>
          </div>
        ) : (
          <>
            {activeOrders.map(order => {
              const stepIndex = getStepIndex(order.status);
              const eta       = etas[order.oid];

              return (
                <div key={order.oid} className="card" style={{ marginBottom:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                    <div>
                      <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1 }}>
                        {order.oid} • {order.time}
                      </p>
                      <p style={{ fontSize:14, color:'var(--text-primary)', marginTop:3 }}>{order.summary}</p>
                    </div>
                    <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:18, color:'var(--accent-blue)' }}>
                      {order.total}
                    </p>
                  </div>

                  {/* ETA - show wait time only, no queue info. Also covers the
                      "arrived" case even though it's not a live GPS estimate —
                      otherwise a stale "X min away" (or nothing at all) could
                      sit on screen after the driver is already at the door. */}
                  {eta && (eta.showLive || order.status === 'Driver Arrived') && (
                    <div style={{ background:'#0a1530', border:'1px solid #1e3a6a', borderRadius:10, padding:'10px 14px', marginBottom:12, display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:18 }}>{order.status === 'Driver Arrived' ? '📍' : '🕐'}</span>
                      <div>
                        <p style={{ fontSize:13, color:'var(--accent-blue)', fontWeight:500 }}>{eta.message}</p>
                        <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
                          {order.status === 'Driver Arrived' ? "They'll confirm delivery when you hand off your order" : 'Estimated arrival time'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Contact your driver — messaging only needs a driver assigned; the
                      masked call number is a separate, independent thing that can be
                      unavailable (no phone on file, relay setup failed) without blocking chat. */}
                  {order.assigned && (
                    <div style={{ background:'#0a1530', border:'1px solid #1e3a6a', borderRadius:10, padding:'10px 14px', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
                      <div>
                        <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:2 }}>Contact your driver</p>
                        <p style={{ fontSize:14, color:'var(--accent-blue)', fontWeight:500 }}>{order.relayNumber || 'Message in-app'}</p>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        {order.relayNumber && (
                          <a href={`tel:${order.relayNumber}`} style={{ padding:'8px 12px', background:'#0a2010', color:'var(--accent-green)', border:'1px solid #0a3020', borderRadius:8, fontSize:12, textDecoration:'none' }}>📞 Call</a>
                        )}
                        <button onClick={() => setChatOid(order.oid)} style={{ position:'relative', padding:'8px 12px', background:'#0a2010', color:'var(--accent-green)', border:'1px solid #0a3020', borderRadius:8, fontSize:12, cursor:'pointer' }}>
                          💬 Message
                          {unread.orderThreads[order.oid] > 0 && (
                            <span style={{ position:'absolute', top:-6, right:-6, background:'var(--accent-red)', color:'#fff', borderRadius:'50%', width:17, height:17, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontFamily:'DM Sans,sans-serif' }}>
                              {unread.orderThreads[order.oid]}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Status badge */}
                  <div style={{ display:'inline-block', marginBottom:14, background:'#0a1530', border:'1px solid #1e3a6a', color:'var(--accent-blue)', borderRadius:10, padding:'6px 14px', fontSize:12 }}>
                    {order.status || 'Pending'}
                    {order.assigned && ` • Driver assigned`}
                  </div>

                  {/* Progress steps */}
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {STATUSES.map((step, i) => (
                      <div key={step} style={{ display:'flex', gap:12, alignItems:'center' }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background: i <= stepIndex ? 'var(--accent-blue)' : 'var(--border-mid)', border: i <= stepIndex ? 'none' : '1px solid var(--border-hover)' }} />
                        <span style={{ fontSize:13, color: i <= stepIndex ? 'var(--text-primary)' : 'var(--text-muted)' }}>{step}</span>
                        {i === stepIndex && (
                          <span style={{ fontSize:10, color:'var(--accent-blue)', background:'#0a1530', border:'1px solid #1e3a6a', borderRadius:20, padding:'2px 8px', marginLeft:4 }}>Now</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Past Orders */}
            {deliveredOrders.length > 0 && (
              <>
                <p className="section-label" style={{ marginTop:8, marginBottom:8 }}>Recent Orders</p>
                {deliveredOrders.map(order => (
                  <div key={order.oid} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid var(--bg-card)' }}>
                    <div>
                      <p style={{ fontSize:12, color:'var(--text-secondary)' }}>{order.oid} • {order.time}</p>
                      <p style={{ fontSize:13, color:'var(--text-primary)', marginTop:2 }}>{order.summary}</p>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:15, color: order.status === 'Attempted Delivery' || order.status === 'Cancelled' ? 'var(--accent-red)' : 'var(--accent-green)' }}>
                        {order.total}
                      </p>
                      <span className={`pill ${order.status === 'Cancelled' ? 'pill-cancelled' : order.status === 'Attempted Delivery' ? 'pill-pending' : 'pill-delivered'}`} style={{ marginTop:4, display:'inline-block' }}>
                        {order.status === 'Cancelled' ? 'Cancelled' : order.status === 'Attempted Delivery' ? 'Attempted' : 'Delivered'}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      <div className="nav-bar">
        <button className="nav-item" onClick={() => navigate('/dashboard')}><span style={{ fontSize:17 }}>🏠</span>Home</button>
        <button className="nav-item" onClick={() => navigate('/order')}><span style={{ fontSize:17 }}>🛒</span>Order</button>
        <button className="nav-item" onClick={() => navigate('/rewards')}><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
        <button className="nav-item active"><span style={{ fontSize:17 }}>🕐</span>Track</button>
      </div>

      {chatOid && (
        <ChatThread
          title="Message your driver"
          selfRole="customer"
          fetchUrl={`/api/orders/${chatOid}/messages`}
          postUrl={`/api/orders/${chatOid}/messages`}
          onClose={() => { setChatOid(null); fetchUnread(); }}
        />
      )}
    </div>
  );
}
